import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertTriangle, CheckCircle, MapPin, XCircle } from 'lucide-react'
import { workOrdersApi } from '../../services/api'
import type { VerificaFrazionarioResult, CorrezioneWO } from '../../types/index'

/**
 * Ripara CAP/Provincia — procedura in 5 passi sulle pratiche selezionate:
 *  1. confronto con l'elenco uffici postali abilitati (PDF) → scelta campo per campo → Procedi
 *  2. verifica vs Poste live (blia.it) → scelta campo per campo → Procedi
 *  3. Nominatim per CAP/provincia ancora mancanti → scelta pratica per pratica → Procedi
 *  4. verifica finale indirizzo/città/provincia su poste.it (Vieni in Poste) → scelta campo per campo → Procedi
 *  5. coordinate GPS sulle pratiche selezionate: quelle dell'ufficio da poste.it, Nominatim solo come ripiego
 * Nulla viene scritto sul DB senza aver premuto Procedi.
 */

interface Props {
  ids: number[]
  onClose: () => void
}

type Step = 'pdf_loading' | 'pdf' | 'poste_loading' | 'poste' | 'nom_loading' | 'nom' | 'pit_loading' | 'pit' | 'gps' | 'fine'

const LABEL_CAMPO: Record<string, string> = {
  indirizzo: 'indirizzo', comune: 'comune', cap: 'CAP', regione: 'regione', provincia: 'provincia',
}

const VUOTO: VerificaFrazionarioResult = { discrepanze: [], non_trovati: [], senza_frazionario: [], totale_ok: 0 }

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms))

// Una riga per campo da correggere (passi PDF e Poste)
const righeCampi = (res: VerificaFrazionarioResult) => res.discrepanze.flatMap(d => d.campi_diversi.map(c => ({
  key: `${d.wo_id}-${c.campo}`, wo_id: d.wo_id, numero: d.numero_progressivo,
  frazionario: d.frazionario, ufficio: d.nome_ufficio_pdf, campo: c.campo, attuale: c.attuale, suggerito: c.suggerito,
})))

const correzioniDa = (righe: ReturnType<typeof righeCampi>, sel: Set<string>): CorrezioneWO[] => {
  const byWo = new Map<number, CorrezioneWO>()
  for (const r of righe) {
    if (!sel.has(r.key)) continue
    const c = byWo.get(r.wo_id) ?? { wo_id: r.wo_id }
    ;(c as any)[r.campo] = r.suggerito
    byWo.set(r.wo_id, c)
  }
  return Array.from(byWo.values())
}

export default function RiparaIndirizziWizard({ ids, onClose }: Props) {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<Step>('pdf_loading')
  const [busy, setBusy] = useState(false)
  const annullato = useRef(false)

  const [pdf, setPdf] = useState<VerificaFrazionarioResult>(VUOTO)
  const [selPdf, setSelPdf] = useState<Set<string>>(new Set())
  const [poste, setPoste] = useState<VerificaFrazionarioResult>(VUOTO)
  const [selPoste, setSelPoste] = useState<Set<string>>(new Set())
  const [pit, setPit] = useState<VerificaFrazionarioResult>(VUOTO)
  const [selPit, setSelPit] = useState<Set<string>>(new Set())
  const [nom, setNom] = useState<VerificaFrazionarioResult>(VUOTO)
  const [selNom, setSelNom] = useState<Set<number>>(new Set())
  const [progress, setProgress] = useState<{ fatte: number; totale: number } | null>(null)
  const [applicate, setApplicate] = useState({ pdf: 0, poste: 0, nom: 0, pit: 0 })
  const [gpsErrori, setGpsErrori] = useState<{ id: number; motivo: string }[]>([])
  const [gpsFonti, setGpsFonti] = useState({ poste: 0, nominatim: 0 })
  const [showNonTrovati, setShowNonTrovati] = useState(false)

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['work-orders'] })
    queryClient.invalidateQueries({ queryKey: ['work-orders-standard'] })
  }

  // ── Passo 1: confronto PDF ────────────────────────────────────────────────
  useEffect(() => {
    workOrdersApi.riparaVerificaPdf(ids)
      .then(res => {
        const d = res.data as VerificaFrazionarioResult
        setPdf(d)
        setSelPdf(new Set(d.discrepanze.flatMap(x => x.campi_diversi.map(c => `${x.wo_id}-${c.campo}`))))
        setStep('pdf')
      })
      .catch(() => { toast.error('Errore nel confronto con il PDF uffici postali'); onClose() })
    return () => { annullato.current = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const righePdf = useMemo(() => righeCampi(pdf), [pdf])
  const righePoste = useMemo(() => righeCampi(poste), [poste])
  const righePit = useMemo(() => righeCampi(pit), [pit])

  // Verifica a lotti di 10 pratiche (servizi esterni lenti) per mostrare l'avanzamento
  const verificaALotti = async (fn: (ids: number[]) => Promise<{ data: unknown }>) => {
    const acc: VerificaFrazionarioResult = { discrepanze: [], non_trovati: [], senza_frazionario: [], totale_ok: 0 }
    for (let i = 0; i < ids.length && !annullato.current; i += 10) {
      setProgress({ fatte: i, totale: ids.length })
      const d = (await fn(ids.slice(i, i + 10))).data as VerificaFrazionarioResult
      acc.discrepanze.push(...d.discrepanze)
      acc.non_trovati.push(...d.non_trovati)
      acc.senza_frazionario.push(...d.senza_frazionario)
      acc.totale_ok += d.totale_ok
    }
    setProgress(null)
    return acc
  }

  const procediPdf = async () => {
    setBusy(true)
    try {
      const correzioni = correzioniDa(righePdf, selPdf)
      if (correzioni.length) {
        await workOrdersApi.applicaCorrezioni(correzioni, 'Dati corretti da elenco uffici postali abilitati (PDF)')
        setApplicate(a => ({ ...a, pdf: correzioni.length }))
        refresh()
      }
    } catch {
      toast.error('Errore nel salvataggio delle correzioni dal PDF')
      setBusy(false)
      return
    }
    setBusy(false)
    await avviaPoste()
  }

  // ── Passo 2: verifica vs Poste live (blia.it) ─────────────────────────────
  const avviaPoste = async () => {
    setStep('poste_loading')
    let acc = VUOTO
    try {
      acc = await verificaALotti(workOrdersApi.verificaFrazionarioPoste)
    } catch {
      setProgress(null)
      toast.error('Errore nella verifica vs Poste (blia.it)')
    }
    setPoste(acc)
    setSelPoste(new Set(righeCampi(acc).map(r => r.key)))
    setStep('poste')
  }

  const procediPoste = async () => {
    setBusy(true)
    try {
      const correzioni = correzioniDa(righePoste, selPoste)
      if (correzioni.length) {
        await workOrdersApi.applicaCorrezioni(correzioni, 'Dati corretti da verifica vs Poste live (blia.it)')
        setApplicate(a => ({ ...a, poste: correzioni.length }))
        refresh()
      }
    } catch {
      toast.error('Errore nel salvataggio delle correzioni Poste')
      setBusy(false)
      return
    }
    setBusy(false)
    await avviaNominatim()
  }

  // ── Passo 3: Nominatim CAP/provincia mancanti ─────────────────────────────
  const avviaNominatim = async () => {
    setStep('nom_loading')
    const acc: VerificaFrazionarioResult = { discrepanze: [], non_trovati: [], senza_frazionario: [], totale_ok: 0 }
    let afterId = 0
    try {
      while (!annullato.current) {
        const res = await workOrdersApi.riparaCapProvincia(ids, afterId)
        const d = res.data
        acc.discrepanze.push(...d.discrepanze)
        acc.non_trovati.push(...d.non_trovati)
        afterId = d.ultimo_id
        const fatte = acc.discrepanze.length + acc.non_trovati.length
        setProgress({ fatte, totale: fatte + d.rimasti })
        if (d.rimasti === 0 || (d.discrepanze.length === 0 && d.non_trovati.length === 0)) break
      }
    } catch {
      toast.error('Errore nella ricerca CAP/provincia su Nominatim')
    }
    setProgress(null)
    setNom(acc)
    setSelNom(new Set(acc.discrepanze.map(d => d.wo_id)))
    setStep('nom')
  }

  const procediNom = async () => {
    setBusy(true)
    try {
      const correzioni: CorrezioneWO[] = nom.discrepanze
        .filter(d => selNom.has(d.wo_id))
        .map(d => {
          const c: CorrezioneWO = { wo_id: d.wo_id }
          for (const x of d.campi_diversi) {
            if (x.campo === 'cap') c.cap = x.suggerito
            if (x.campo === 'provincia') c.provincia = x.suggerito
          }
          return c
        })
      if (correzioni.length) {
        await workOrdersApi.applicaCorrezioni(correzioni, 'CAP/provincia ricavati da Nominatim (Ripara CAP/Prov.)')
        setApplicate(a => ({ ...a, nom: correzioni.length }))
        refresh()
      }
    } catch {
      toast.error('Errore nel salvataggio CAP/provincia')
      setBusy(false)
      return
    }
    setBusy(false)
    await avviaPosteIt()
  }

  // ── Passo 4: verifica finale indirizzo/città su poste.it ──────────────────
  const avviaPosteIt = async () => {
    setStep('pit_loading')
    let acc = VUOTO
    try {
      acc = await verificaALotti(workOrdersApi.verificaPosteIt)
    } catch {
      setProgress(null)
      toast.error('Errore nella verifica su poste.it')
    }
    setPit(acc)
    setSelPit(new Set(righeCampi(acc).map(r => r.key)))
    setStep('pit')
  }

  const procediPosteIt = async () => {
    setBusy(true)
    try {
      const correzioni = correzioniDa(righePit, selPit)
      if (correzioni.length) {
        await workOrdersApi.applicaCorrezioni(correzioni, 'Indirizzo/città corretti da poste.it (Vieni in Poste)')
        setApplicate(a => ({ ...a, pit: correzioni.length }))
        refresh()
      }
    } catch {
      toast.error('Errore nel salvataggio delle correzioni poste.it')
      setBusy(false)
      return
    }
    setBusy(false)
    await avviaGps()
  }

  // ── Passo 5: coordinate GPS ───────────────────────────────────────────────
  const avviaGps = async () => {
    setStep('gps')
    const errori: { id: number; motivo: string }[] = []
    // 1) coordinate ufficiali dell'ufficio da poste.it (lotti da 10)
    const daNominatim: number[] = []
    let daPoste = 0
    for (let i = 0; i < ids.length && !annullato.current; i += 10) {
      setProgress({ fatte: i, totale: ids.length })
      const lotto = ids.slice(i, i + 10)
      try {
        const d = (await workOrdersApi.gpsPosteIt(lotto)).data as { aggiornati: number[]; non_trovati: number[] }
        daPoste += d.aggiornati.length
        daNominatim.push(...d.non_trovati)
      } catch {
        daNominatim.push(...lotto)
      }
    }
    // 2) ripiego Nominatim per quelle non trovate su poste.it
    for (let i = 0; i < daNominatim.length && !annullato.current; i++) {
      setProgress({ fatte: ids.length - daNominatim.length + i, totale: ids.length })
      try {
        await workOrdersApi.geocode(daNominatim[i])
      } catch (e: any) {
        errori.push({ id: daNominatim[i], motivo: e?.response?.data?.detail || 'errore' })
      }
      await sleep(1100) // rate limit Nominatim
    }
    setProgress(null)
    setGpsFonti({ poste: daPoste, nominatim: daNominatim.length - errori.length })
    setGpsErrori(errori)
    refresh()
    setStep('fine')
  }

  const numeroDi = (id: number) =>
    pdf.discrepanze.find(d => d.wo_id === id)?.numero_progressivo
    ?? pdf.non_trovati.find(d => d.wo_id === id)?.numero_progressivo
    ?? pdf.senza_frazionario.find(d => d.wo_id === id)?.numero_progressivo
    ?? `#${id}`

  const chiudi = () => { annullato.current = true; onClose() }

  const toggle = <T,>(set: Set<T>, setter: (s: Set<T>) => void, k: T) => {
    const n = new Set(set); n.has(k) ? n.delete(k) : n.add(k); setter(n)
  }

  const passi = ['1. Confronto PDF', '2. Verifica Poste live', '3. CAP/Prov. Nominatim', '4. Verifica poste.it', '5. Coordinate GPS']
  const passoAttivo = step.startsWith('pdf') ? 0 : step.startsWith('poste') ? 1 : step.startsWith('nom') ? 2 : step.startsWith('pit') ? 3 : 4

  const nonTrovatiLista = step === 'pdf' ? pdf.non_trovati : step === 'poste' ? poste.non_trovati
    : step === 'pit' ? pit.non_trovati : nom.non_trovati
  const labelNonTrovati = step === 'pdf' ? 'Frazionario non presente nel PDF'
    : step === 'poste' ? 'Frazionario non trovato su Poste live'
    : step === 'pit' ? 'Frazionario non trovato su poste.it' : 'Non trovate su Nominatim — verificare a mano'

  // Tabella campo per campo (passi PDF e Poste)
  const tabellaCampi = (righe: ReturnType<typeof righeCampi>, sel: Set<string>, setSel: (s: Set<string>) => void, fonte: string) => (
    <TabellaScelta
      colonne={['Pratica', 'Frazionario / Ufficio', 'Campo', 'Attuale', fonte]}
      tutte={righe.length > 0 && sel.size === righe.length}
      onTutte={() => setSel(sel.size === righe.length ? new Set() : new Set(righe.map(r => r.key)))}
      righe={righe.map(r => ({
        key: r.key,
        checked: sel.has(r.key),
        onToggle: () => toggle(sel, setSel, r.key),
        celle: [
          <span className="font-mono">{r.numero}</span>,
          <span><span className="font-mono">{r.frazionario}</span> <span className="text-slate-400">{r.ufficio}</span></span>,
          <span className="badge bg-blue-50 text-blue-700">{LABEL_CAMPO[r.campo] ?? r.campo}</span>,
          <span className="text-slate-500 line-through">{r.attuale || '—'}</span>,
          <span className="text-emerald-700 font-medium">{r.suggerito}</span>,
        ],
      }))}
    />
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(8,11,18,0.72)', backdropFilter: 'blur(4px)' }}>
      <div className="card w-full max-w-4xl animate-fade-up flex flex-col" style={{ maxHeight: '90vh' }}>

        <div className="p-5 border-b shrink-0">
          <div className="flex items-start justify-between">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <MapPin size={16} className="text-blue-600" /> Ripara CAP / Provincia
              <span className="text-xs font-normal text-slate-400">({ids.length} pratiche selezionate)</span>
            </h2>
            <button onClick={chiudi} disabled={busy} className="btn-ghost px-2">✕</button>
          </div>
          <div className="flex gap-2 mt-3 text-xs">
            {passi.map((p, i) => (
              <span key={p} className={`px-2 py-1 rounded ${i === passoAttivo ? 'bg-blue-600 text-white' : i < passoAttivo || step === 'fine' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                {p}
              </span>
            ))}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4 text-sm">
          {(step === 'pdf_loading' || step === 'poste_loading' || step === 'nom_loading' || step === 'pit_loading' || step === 'gps') && (
            <div className="py-10 text-center text-slate-500">
              {step === 'pdf_loading' && 'Confronto con l\'elenco uffici postali abilitati...'}
              {step === 'poste_loading' && 'Verifica su Poste live (blia.it) per frazionario (circa 1 secondo a pratica)...'}
              {step === 'nom_loading' && 'Ricerca CAP/provincia mancanti su Nominatim (circa 1-2 secondi a pratica)...'}
              {step === 'pit_loading' && 'Verifica finale indirizzo/città su poste.it (circa mezzo secondo a pratica)...'}
              {step === 'gps' && 'Coordinate GPS: prima dall\'ufficio su poste.it, poi Nominatim per le pratiche non trovate...'}
              {progress && <div className="mt-2 font-mono">{progress.fatte} / {progress.totale}</div>}
            </div>
          )}

          {step === 'pdf' && (
            <>
              <p className="text-xs text-slate-500">
                <span className="text-amber-600 font-medium">{pdf.discrepanze.length} pratiche con differenze</span> ·{' '}
                <span className="text-emerald-600 font-medium">{pdf.totale_ok} già corrette</span> ·{' '}
                {pdf.non_trovati.length} frazionario non nel PDF · {pdf.senza_frazionario.length} senza frazionario.
                {' '}Scegli i campi da aggiornare, poi <b>Procedi</b>.
              </p>
              {righePdf.length > 0 ? (
                tabellaCampi(righePdf, selPdf, setSelPdf, 'Dal PDF')
              ) : (
                <Ok testo="Nessuna differenza rispetto al PDF." />
              )}
            </>
          )}

          {step === 'poste' && (
            <>
              <p className="text-xs text-slate-500">
                <span className="text-amber-600 font-medium">{poste.discrepanze.length} pratiche con differenze</span> ·{' '}
                <span className="text-emerald-600 font-medium">{poste.totale_ok} già corrette</span> ·{' '}
                {poste.non_trovati.length} frazionario non trovato · {poste.senza_frazionario.length} senza frazionario.
                {' '}Dati aggiornati dopo il passo 1. Scegli i campi da aggiornare, poi <b>Procedi</b>.
              </p>
              {righePoste.length > 0 ? (
                tabellaCampi(righePoste, selPoste, setSelPoste, 'Da Poste live')
              ) : (
                <Ok testo="Nessuna differenza rispetto a Poste live." />
              )}
            </>
          )}

          {step === 'nom' && (
            <>
              <p className="text-xs text-slate-500">
                <span className="text-amber-600 font-medium">{nom.discrepanze.length} pratiche con CAP/provincia trovati</span> ·{' '}
                {nom.non_trovati.length} non trovate su Nominatim.
                {' '}Vengono riempiti solo i campi vuoti. Scegli le pratiche, poi <b>Procedi</b>.
              </p>
              {nom.discrepanze.length > 0 ? (
                <TabellaScelta
                  colonne={['Pratica', 'Frazionario', 'Nome sito', 'CAP', 'Provincia']}
                  tutte={selNom.size === nom.discrepanze.length}
                  onTutte={() => setSelNom(selNom.size === nom.discrepanze.length ? new Set() : new Set(nom.discrepanze.map(d => d.wo_id)))}
                  righe={nom.discrepanze.map(d => {
                    const cap = d.campi_diversi.find(c => c.campo === 'cap')?.suggerito
                    const prov = d.campi_diversi.find(c => c.campo === 'provincia')?.suggerito
                    return {
                      key: String(d.wo_id),
                      checked: selNom.has(d.wo_id),
                      onToggle: () => toggle(selNom, setSelNom, d.wo_id),
                      celle: [
                        <span className="font-mono">{d.numero_progressivo}</span>,
                        <span className="font-mono">{d.frazionario || '—'}</span>,
                        <span className="text-slate-500">{d.nome_ufficio_pdf || ''}</span>,
                        cap ? <span className="text-emerald-700 font-medium">{cap}</span> : <span className="text-slate-300">invariato</span>,
                        prov ? <span className="text-emerald-700 font-medium">{prov}</span> : <span className="text-slate-300">invariato</span>,
                      ],
                    }
                  })}
                />
              ) : (
                <Ok testo="Nessun CAP/provincia da aggiungere." />
              )}
            </>
          )}

          {step === 'pit' && (
            <>
              <p className="text-xs text-slate-500">
                <span className="text-amber-600 font-medium">{pit.discrepanze.length} pratiche con differenze</span> ·{' '}
                <span className="text-emerald-600 font-medium">{pit.totale_ok} già corrette</span> ·{' '}
                {pit.non_trovati.length} frazionario non trovato · {pit.senza_frazionario.length} senza frazionario.
                {' '}Verifica finale di indirizzo, città e provincia su poste.it. Scegli i campi, poi <b>Procedi</b> per salvare e calcolare le coordinate GPS.
              </p>
              {righePit.length > 0 ? (
                tabellaCampi(righePit, selPit, setSelPit, 'Da poste.it')
              ) : (
                <Ok testo="Indirizzo e città coincidono con poste.it." />
              )}
            </>
          )}

          {(step === 'pdf' || step === 'poste' || step === 'nom' || step === 'pit') && nonTrovatiLista.length > 0 && (
            <div>
              <button onClick={() => setShowNonTrovati(v => !v)}
                className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 font-semibold uppercase tracking-wider">
                <XCircle size={13} className="text-slate-400" />
                {labelNonTrovati} ({nonTrovatiLista.length})
                <span>{showNonTrovati ? '▲' : '▼'}</span>
              </button>
              {showNonTrovati && (
                <ul className="mt-2 border rounded-lg divide-y text-xs">
                  {nonTrovatiLista.map(r => (
                    <li key={r.wo_id} className="px-3 py-1.5">
                      <span className="font-mono">{r.numero_progressivo}</span>
                      <span className="text-slate-400"> · {r.frazionario || '—'}{r.dettaglio ? ` · ${r.dettaglio}` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {step === 'fine' && (
            <div className="space-y-3">
              <Ok testo={`Completato. Correzioni dal PDF: ${applicate.pdf} pratiche · da Poste live: ${applicate.poste} pratiche · CAP/provincia da Nominatim: ${applicate.nom} pratiche · da poste.it: ${applicate.pit} pratiche · Coordinate GPS: ${gpsFonti.poste + gpsFonti.nominatim} su ${ids.length} (${gpsFonti.poste} da poste.it, ${gpsFonti.nominatim} da Nominatim).`} />
              {gpsErrori.length > 0 && (
                <div>
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-700">
                    <AlertTriangle size={13} /> Coordinate non trovate ({gpsErrori.length})
                  </p>
                  <ul className="mt-2 border rounded-lg divide-y text-xs">
                    {gpsErrori.map(e => (
                      <li key={e.id} className="px-3 py-1.5">
                        <span className="font-mono">{numeroDi(e.id)}</span> <span className="text-slate-400">· {e.motivo}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 pb-5 pt-3 border-t flex gap-3 shrink-0">
          {step === 'fine' ? (
            <button onClick={onClose} className="btn-primary flex-1">Chiudi</button>
          ) : (
            <>
              <button onClick={chiudi} disabled={busy} className="btn-secondary flex-1">
                {step === 'pdf' || step === 'pdf_loading' ? 'Annulla (nessuna modifica)' : 'Interrompi'}
              </button>
              {step === 'pdf' && (
                <button onClick={procediPdf} disabled={busy} className="btn-primary flex-1">
                  {busy ? 'Salvo...' : `Procedi${selPdf.size ? ` (applica ${selPdf.size} campi)` : ' senza modifiche'}`}
                </button>
              )}
              {step === 'poste' && (
                <button onClick={procediPoste} disabled={busy} className="btn-primary flex-1">
                  {busy ? 'Salvo...' : `Procedi${selPoste.size ? ` (applica ${selPoste.size} campi)` : ' senza modifiche'}`}
                </button>
              )}
              {step === 'nom' && (
                <button onClick={procediNom} disabled={busy} className="btn-primary flex-1">
                  {busy ? 'Salvo...' : `Procedi${selNom.size ? ` (applica ${selNom.size} pratiche)` : ' senza modifiche'}`}
                </button>
              )}
              {step === 'pit' && (
                <button onClick={procediPosteIt} disabled={busy} className="btn-primary flex-1">
                  {busy ? 'Salvo...' : `Procedi${selPit.size ? ` (applica ${selPit.size} campi)` : ' senza modifiche'} → GPS`}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function Ok({ testo }: { testo: string }) {
  return (
    <div className="flex items-center gap-2 p-4 bg-emerald-50 rounded-lg text-emerald-700 text-sm">
      <CheckCircle size={16} className="shrink-0" /> {testo}
    </div>
  )
}

function TabellaScelta({ colonne, righe, tutte, onTutte }: {
  colonne: string[]
  righe: { key: string; checked: boolean; onToggle: () => void; celle: ReactNode[] }[]
  tutte: boolean
  onTutte: () => void
}) {
  return (
    <div className="border rounded-lg overflow-x-auto text-xs">
      <table className="w-full">
        <thead>
          <tr className="bg-slate-50 border-b">
            <th className="px-3 py-2 w-8"><input type="checkbox" checked={tutte} onChange={onTutte} className="cursor-pointer" /></th>
            {colonne.map(c => <th key={c} className="text-left px-3 py-2 text-slate-600 font-semibold">{c}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {righe.map(r => (
            <tr key={r.key} className="hover:bg-slate-50 cursor-pointer" onClick={r.onToggle}>
              <td className="px-3 py-2">
                <input type="checkbox" checked={r.checked} onChange={r.onToggle}
                  onClick={e => e.stopPropagation()} className="cursor-pointer" />
              </td>
              {r.celle.map((c, i) => <td key={i} className="px-3 py-2">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
